/**
 * request 网络请求工具
 * 更详细的 api 文档: https://github.com/umijs/umi-request
 */
import { extend } from 'umi-request';
import { notification, message } from 'antd';
// import qs from 'qs';
// import 'yet-another-abortcontroller-polyfill';
// import { history } from 'umi';
import webStorage from '@/utils/storageManager';
import {
  getToken,
  setToken,
  getRefreshToken,
  getTokenRefreshTime,
} from '@/utils/token';
// import { brandCodeMap } from '@/constants';

// let requestList = new Set();

const codeMessage = {
  200: '服务器成功返回请求的数据。',
  201: '新建或修改数据成功。',
  202: '一个请求已经进入后台排队（异步任务）。',
  204: '删除数据成功。',
  400: '发出的请求有错误，服务器没有进行新建或修改数据的操作。',
  401: '用户没有权限（令牌、用户名、密码错误）。',
  403: '用户得到授权，但是访问是被禁止的。',
  404: '发出的请求针对的是不存在的记录，服务器没有进行操作。',
  406: '请求的格式不可得。',
  410: '请求的资源被永久删除，且不会再得到的。',
  422: '当创建一个对象时，发生一个验证错误。',
  500: '服务器发生错误，请检查服务器。',
  502: '网关错误。',
  503: '服务不可用，服务器暂时过载或维护。',
  504: '网关超时。',
};

const API_OAUTH = '/oauth/token'; // api oauth鉴权
const API_USER = '/users'; // api 获取用户

// 是否正在刷新的标记
let isRefreshing = false;
let isToLogin = false;
// 重试队列，每一项将是一个待执行的函数形式
let requests = [];

/**
 * 异常处理程序
 */
const errorHandler = (error) => {
  showErrorMessage(error);
};
/**
 * 配置request请求时的默认参数
 */
const umiRequest = extend({
  errorHandler, // 默认错误处理
  // 'credentials' 发送带凭据的请求
  // 为了让浏览器发送包含凭据的请求（即使是跨域源），需要设置 credentials: 'include'
  // 如果只想在请求URL与调用脚本位于同一起源处时发送凭据，请添加credentials: 'same-origin'
  // 要改为确保浏览器不在请求中包含凭据，请使用credentials: 'omit'
  credentials: 'include', // default : same-origin
  headers: {
    'Access-Control-Allow-Credentials': true,
    // 'Content-Type': 'application/json',
  },
});

// 刷新token
const refreshToken = () => {
  // instance是当前request.js中已创建的axios实例
  const formData = new FormData();
  formData.append('grant_type', 'refresh_token');
  formData.append('response_type', 'refresh_token');
  formData.append('refresh_token', getRefreshToken());
  // console.log('执行refresh_token');
  return umiRequest
    .post(API_OAUTH, {
      data: formData,
    })
    .then(function (response) {
      // console.log('refreshToken_success', response);
      return response;
    })
    .catch(function (error) {
      console.error('refreshToken_error', error);
      return error;
    });
};

const showErrorMessage = (error) => {
  const { response, data } = error || {};
  const { status, statusText, url } = response || {};
  const errortext = codeMessage[status] || statusText;
  if (error.request.options.responseType === 'blob') {
    let reader = new FileReader();
    reader.readAsText(data, 'utf-8');
    reader.onload = function () {
      if (status > 399 && status < 500 && status !== 413) {
        let blobData = JSON.parse(reader.result);
        if (!blobData) {
          return;
        }
        message.destroy();
        message.error(blobData.message || blobData.error || blobData.errorMsg);
      }
    };
  }
  if (status > 399 && status < 500 && status !== 413) {
    if (!data && status !== 400) {
      return;
    }
    message.destroy();
    message.error(data.message || data.error || data.errorMsg);
  } else if (status > 499) {
    notification.error({
      message: `请求错误 ${status}: ${url}`,
      description: errortext,
    });
  }
  throw response;
};

/**
 * 获取请求url前缀
 * @param {String} serviceUrl service层请求的url
 */
const getRequestPrefix = (serviceUrl) => {
  let requestPrefix = `${process.env.BASE_URL}`;

  if (serviceUrl.includes(API_OAUTH) || serviceUrl.includes(API_USER)) {
    requestPrefix = `${process.env.AUTH_URL}`;
  }
  // console.log('💢felix => getRequestPrefix => requestPrefix', requestPrefix);
  return requestPrefix;
};

// request拦截器, 改变url 或 options.
// 在请求发起前拦截每个请求，判断token的有效时间是否已经过期，若已过期，则将请求挂起，先刷新token后再继续请求。
umiRequest.interceptors.request.use(
  (url, options) => {
    webStorage.deleteAllExpires();
    if (
      !getTokenRefreshTime() &&
      window.location.href.indexOf('/login') === -1 &&
      !isToLogin
    ) {
      isToLogin = true;
      window.location.href = '/login';
      return null;
    }
    if (window.location.href.indexOf('/login') > -1) {
      isToLogin = false;
    }
    let optionsCustom = options;
    let requestUrl = getRequestPrefix(url) + url;
    const apiAuthToken = webStorage.get('AUTHORIZATION');
    // console.log('💢felix => apiAuthToken', apiAuthToken);
    const apiToken = `Bearer ${getToken()}`;
    // console.log('💢felix => apiToken', apiToken);
    let headers = {};
    optionsCustom.headers['Authorization'] = url.includes(API_OAUTH)
      ? apiAuthToken
      : apiToken;
    headers['Authorization'] = url.includes(API_OAUTH)
      ? apiAuthToken
      : apiToken;

    if (webStorage.get('TENANT_ID')) {
      optionsCustom.headers['Tenant-Id'] = webStorage.get('TENANT_ID');
      headers['Tenant-Id'] = webStorage.get('TENANT_ID');
    }
    if (!getToken() && !url.includes(API_OAUTH)) {
      //如果token已经过期了，先拿token
      if (!isRefreshing) {
        isRefreshing = true;
        return refreshToken()
          .then((res) => {
            setToken(res);
            const token = `Bearer ${res.access_token}`;
            // console.log('💢felix => apiToken', apiToken);

            optionsCustom.headers['Authorization'] = token;
            headers['Authorization'] = token;
          })
          .then(() => {
            requests.forEach((cb) => cb(headers));
            requests = [];
            return {
              url: requestUrl,
              options: optionsCustom,
            };
          })
          .finally(() => {
            isRefreshing = false;
          });
      }
      return new Promise((resolve) => {
        requests.push((op) =>
          resolve({
            url: requestUrl,
            options: {
              ...optionsCustom,
              headers: { ...optionsCustom.headers, ...op },
            },
          }),
        );
      });
    }
    return {
      url: requestUrl,
      options: optionsCustom,
    };
  },
  { global: true },
);

// response拦截器, 处理response
umiRequest.interceptors.response.use(
  // eslint-disable-next-line no-unused-vars
  async (response, options) => {
    const { status, url } = response;
    if (status === 401 || status === 402) {
      if (url.includes(API_OAUTH)) {
        window.location.href = '/login';
      }
    }
    return response;
  },
  (error) => {
    return Promise.reject(error);
  },
);

export default umiRequest;
